import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import CompanyDashboard from "@/pages/CompanyDashboard";
import CompanyOnboarding from "@/pages/CompanyOnboarding";
import FunderDashboard from "@/pages/FunderDashboard";
import FunderOnboarding from "@/pages/FunderOnboarding";
import AdminDashboard from "@/pages/AdminDashboard";
import DemoExperience from "@/pages/DemoExperience";

// Protected route component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    if (user?.role === 'COMPANY') return <Navigate to="/company" replace />;
    if (user?.role === 'FUNDER') return <Navigate to="/funder" replace />;
    if (user?.role === 'ADMIN') return <Navigate to="/admin" replace />;
    return <Navigate to="/" replace />;
  }

  return children;
}

// Redirect authenticated users away from auth pages
function AuthRoute({ children }) {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === 'COMPANY') return <Navigate to="/company" replace />;
    if (user?.role === 'FUNDER') return <Navigate to="/funder" replace />;
    if (user?.role === 'ADMIN') return <Navigate to="/admin" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      
      <Route path="/login" element={<AuthRoute><AuthPage /></AuthRoute>} />
      <Route path="/register" element={<AuthRoute><AuthPage /></AuthRoute>} />

      {/* Demo route - must be before role-specific routes */}
      <Route path="/demo" element={<ProtectedRoute allowedRoles={['COMPANY', 'FUNDER', 'ADMIN']}><DemoExperience /></ProtectedRoute>} />

      <Route path="/company/onboarding" element={<ProtectedRoute allowedRoles={['COMPANY']}><CompanyOnboarding /></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute allowedRoles={['COMPANY']}><CompanyDashboard /></ProtectedRoute>} />
      <Route path="/company/*" element={<ProtectedRoute allowedRoles={['COMPANY']}><CompanyDashboard /></ProtectedRoute>} />

      <Route path="/funder/onboarding" element={<ProtectedRoute allowedRoles={['FUNDER']}><FunderOnboarding /></ProtectedRoute>} />
      <Route path="/funder" element={<ProtectedRoute allowedRoles={['FUNDER']}><FunderDashboard /></ProtectedRoute>} />
      <Route path="/funder/*" element={<ProtectedRoute allowedRoles={['FUNDER']}><FunderDashboard /></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminDashboard /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
